import type { ApiResponse } from '../../api/contract';

type ApiTransactionSummary = ApiResponse<'/api/v1/transactions', 'get'>['data'][number];
type ApiTransactionDetail = ApiResponse<'/api/v1/transactions/{id}', 'get'>['data'];

export type TransactionOutcome = ApiTransactionSummary['outcome'];
export type TransactionStatus = ApiTransactionSummary['persistedStatus'];
export type TransactionReversalState = ApiTransactionSummary['reversalState'];

export interface TransactionFailure {
  readonly code: string;
  readonly summary: readonly string[];
}

export type TransactionSummary = Omit<ApiTransactionSummary, 'failure'> & {
  readonly failure: TransactionFailure | null;
};

export type TransactionCalculation = NonNullable<ApiTransactionDetail['calculation']>;
export type TransactionLedgerEntry = ApiTransactionDetail['commissionLedgerEntries'][number];

export interface TransactionRequestTrace {
  readonly fieldCount: number;
  readonly trace: string | null;
}

export type TransactionIdempotency = Omit<ApiTransactionDetail['idempotency'], 'key'> & {
  /** Irreversible display hint; the replay key is intentionally discarded by the adapter. */
  readonly keyHint: string;
};

export type TransactionDetail = Omit<
  ApiTransactionDetail,
  'failure' | 'idempotency' | 'requestMetadata'
> & {
  readonly failure: TransactionFailure | null;
  readonly idempotency: TransactionIdempotency;
  readonly requestId: string | null;
  readonly requestTrace: TransactionRequestTrace;
};

export interface TransactionFilters {
  readonly partnerId: string;
  readonly agentId: string;
  readonly memberId: string;
  readonly username: string;
  readonly partnerUserId: string;
  readonly uplineMemberId: string;
  readonly uplineUsername: string;
  readonly gameTypeId: string;
  readonly gameTypeCode: string;
  readonly gameId: string;
  readonly gameCode: string;
  readonly gameRound: string;
  readonly partnerTransactionId: string;
  readonly outcome: TransactionOutcome | '';
  readonly status: TransactionStatus | '';
  readonly reversalState: TransactionReversalState | '';
  readonly currency: string;
  readonly errorCode: string;
  readonly minimumAmount: string;
  readonly maximumAmount: string;
  readonly playFrom: string;
  readonly playTo: string;
  readonly receivedFrom: string;
  readonly receivedTo: string;
}

export interface TransactionListQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly sort: 'receivedAt' | 'playAt';
  readonly filters: TransactionFilters;
}

export function formatTransactionMoney(
  item: Pick<TransactionSummary, 'actualPlayAmount' | 'currencyCode'>,
): string {
  return `${item.currencyCode}\u00a0${item.actualPlayAmount}`;
}

export function bangkokWallTimeToRfc3339(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  if (/(?:Z|[+-]\d{2}:\d{2})$/u.test(trimmed)) return trimmed;
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(\.\d{1,6})?)?$/u.exec(trimmed);
  if (match === null) throw new Error('Expected a Bangkok wall time');
  return `${match[1]}:${match[2] ?? '00'}${match[3] ?? ''}+07:00`;
}

const sensitiveValuePattern =
  /authorization|bearer\s|token|credential|secret|password|raw[ _-]?payload|normalized[ _-]?request|hash/iu;
const safeFailureFields = new Set(['code', 'field', 'reasonCode', 'safeCode', 'validationCode']);

export function toSafeFailure(
  failure: ApiTransactionSummary['failure'],
): TransactionFailure | null {
  if (failure === null) return null;
  if (failure.details === null) return { code: failure.code, summary: [] };
  const summary = Object.entries(failure.details).flatMap(([key, value]) => {
    if (!safeFailureFields.has(key) || (typeof value !== 'string' && typeof value !== 'number'))
      return [];
    const rendered = String(value);
    return sensitiveValuePattern.test(rendered) ? [`${key}: [MASKED]`] : [`${key}: ${rendered}`];
  });
  return { code: failure.code, summary };
}

export function toSafeTrace(metadata: Readonly<Record<string, unknown>>): TransactionRequestTrace {
  const trace = typeof metadata['trace'] === 'string' ? metadata['trace'] : null;
  return {
    fieldCount: Object.keys(metadata).length,
    trace:
      trace === null ? null : sensitiveValuePattern.test(trace) ? '[MASKED]' : trace.slice(0, 120),
  };
}

export function irreversibleKeyHint(_key: string): string {
  return '••••••••';
}

export function safeTransactionText(value: string, fallback = '[MASKED]'): string {
  return sensitiveValuePattern.test(value) ? fallback : value.slice(0, 240);
}
